/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import {
  Asset,
  EntityStatus,
  OperationStatus,
  TimeLineEventType,
  TimeLineType,
  Timeline,
  TimelineEvent,
  TimelineResources,
  TimelineStep,
  TrackingFamily,
} from '@facephi/inphinite-common';
import { Label, Spinner, useTimezone } from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { AssetFile } from './AssetFile';
import { ModalOperationsDetail } from './ModalOperationsDetail';
import { FamilyTimeline, TimelineWrapper } from './Styles';
import { useTimeLine } from '../../hooks/useTimeline';
import { ignoredValidations, manualValidations } from '../../state/model';
type IProps = {
  onboardingId: string;
  disabled?: boolean;
  family?: string;
};
export const OperationTimeline = ({
  onboardingId,
  disabled,
  family,
}: IProps) => {
  const { events, eventMessage, dataEventMessage, loadingEvents } = useTimeLine(
    onboardingId,
    family,
  );

  const [item, setItem] = useState<Asset | null>();
  const { formatTimezone } = useTimezone();
  const { t } = useTranslation();
  if (loadingEvents) {
    return <Spinner insideComponent />;
  }
  const isDataEvent = (type: TimeLineType): boolean =>
    type === TimeLineType.stepChange || type === TimeLineType.stepResult;

  return (
    <TimelineWrapper flex="1">
      <Timeline>
        <FamilyTimeline
          color={family === TrackingFamily.ONBOARDING ? 'purple' : 'blue'}
        >
          <Label
            color={
              family === TrackingFamily.ONBOARDING ? 'purple400' : 'blue400'
            }
            fontSize="12"
            semibold={true}
          >
            {t(family as string)}
          </Label>
        </FamilyTimeline>

        {events
          .filter(
            (item: TimelineEvent) =>
              !ignoredValidations.includes(item.metadata?.mode as string),
          )
          .map((item: TimelineEvent, index: number) => {
            const itemDate = item?.date || item?.clientTimestamp;
            const source = manualValidations.includes(
              (item.metadata.source as string)?.toLowerCase(),
            )
              ? t('Morphological validation')
              : (item.metadata.source as string);
            return (
              <TimelineStep
                testId="timeline-step"
                message={
                  isDataEvent(item.metadata.type as TimeLineType)
                    ? dataEventMessage(
                        item.metadata.type as TimeLineType,
                        item.metadata.stepId as string,
                        item.metadata.stepType as string,
                        item.metadata.stepResultReason as string,
                        item.metadata.stepResultStatus as OperationStatus,
                      )
                    : eventMessage(
                        item.metadata.event as TimeLineEventType,
                        item.metadata.screen as string,
                        item.metadata.value as string,
                        item.metadata.type as string,
                        item.metadata.reason as string,
                        item.metadata?.mode as string,
                        item.metadata.diagnostic as any,
                        item.metadata.authStatus as any,
                        item.metadata.status as string,
                        source || '',
                        item.metadata.walletId as string,
                        item.metadata.credentialType as string,
                      )
                }
                isSuccess={
                  (item.metadata.type === TimeLineType.operationResult &&
                    !item.metadata.reason &&
                    item.metadata.status === EntityStatus.succeeded) ||
                  ((item.metadata.type === TimeLineType.securityInfoData &&
                    item.metadata.succeed) as boolean) ||
                  (item.metadata.type === TimeLineType.stepResult &&
                    !item.metadata.stepResultReason &&
                    item.metadata.stepResultStatus ===
                      OperationStatus.succeeded)
                }
                isError={
                  (item.metadata.type === TimeLineType.operationResult &&
                    item.metadata.reason !== null) ||
                  (item.metadata.type === TimeLineType.operationResult &&
                    item.metadata.status !== EntityStatus.succeeded) ||
                  (item.metadata.type === TimeLineType.securityInfoData &&
                    !item.metadata.succeed) ||
                  (item.metadata.type === TimeLineType.stepResult &&
                    item.metadata.stepResultStatus !==
                      OperationStatus.succeeded)
                }
                timeUpdate={formatTimezone(itemDate as string)}
                separation={'20px'}
                key={index}
              >
                {item.metadata.type === TimeLineType.stepResult &&
                  item.metadata.stepResultReason && (
                    <Label fontSize="12">
                      {t(item.metadata.stepResultReason as string)}
                    </Label>
                  )}
                {item.metadata.type === TimeLineType.asset && (
                  <TimelineResources
                    imageComponent={
                      <AssetFile asset={item.metadata as Asset} />
                    }
                    name={item.metadata.type}
                    onClick={() => setItem(item.metadata as Asset)}
                    disabled={disabled}
                  />
                )}
              </TimelineStep>
            );
          })}
      </Timeline>
      {item && (
        <ModalOperationsDetail
          source={item.url}
          show={!!item}
          mimeType={item.contentType}
          title={item.type}
          onChangeShow={(value) => !value && setItem(null)}
        />
      )}
    </TimelineWrapper>
  );
};
